<template>
  <section :aria-label="compoundScopeText" class="zoomWrapper">
    <div class="column w-full leftColumn">
      <div id="zoomControls" class="row align-center">
        <button @click="saveToLabBook" class="ml-0 inline-flex items-center btn btn-text focus:outline-none ripple">
          <span>
            <img src="../assets/live_data_icons/SaveToLabBook-24px.svg" alt="" class="btn-icon btn-icon-text" />
          </span>
          {{ saveText }}
        </button>
      </div>
      <div v-if="displayMedia" class="row mediaName">
        <b>{{ displayMedia.title }}</b>
      </div>
      <div id="imageWrapper" ref="imageWrapper">
        <canvas ref="view" id="scopeView" :aria-label="displayMedia && displayMedia.title" :class="panCursor"
          @pointerdown="startPan" @pointermove="movePan" @pointerup="endPan" @pointercancel="endPan" />
        <!-- Videos are drawn into the canvas frame by frame, so the player
             itself stays out of the way. -->
        <video v-if="isVideo" ref="video" id="scopeSource" type="video/mp4" muted loop playsinline
          crossorigin="anonymous">
          <source :src="S3URL + PATH + displayMedia.i + MP4 + '#t=0.01'" type="video/mp4" />
        </video>
      </div>
    </div>
    <div id="imagesList" class="column">
      <img v-for="image in images" :key="image.id" :alt="image.title" :data-media-id="image.id"
        crossorigin="anonymous" decoding="async" @mouseenter="prefetch(image)" @focus="prefetch(image)"
        :class="['imageThumbnail', { 'selectedThumbnail': displayMedia && image.id == displayMedia.id }]"
        @click="selectImage(image)" :src="S3URL + PATH + image.i + THUMB + (image.t == 'static' ? JPG : F4V)"
        tabindex="0" @keyup.enter="selectImage(image)" />
    </div>
  </section>
</template>
<script>
import { mapGetters } from 'vuex';
import Constants from '../enums/Constants.js';
import MicroscopyMiddlewareServices from '../services/MicroscopyMiddlewareServices.js';
import { GET_CURRENT_MEDIA, GET_CURRENT_SPECIES, GET_SPECIAL_MEDIA_LIST } from '../store/store-types.js';
import html2canvas from 'html2canvas';
import * as LabBookMiddleware from './LabBook/scripts/LabBookMiddleware.js';
import ImageMixin from '../mixins/ImageMixin.js';
import { entry } from '../services/xapi.js';
import xapiEntryTypes from '../enums/xapiEntryTypes.js';
import BiologyLabEnum from '../enums/BiologyLabEnum.js';
import NotificationMixin from '../mixins/NotificationMixin.js';
import {
  scope,
  useImage,
  magnificationZoom,
  separation,
  blurAmount,
  brightnessAmount,
} from '../services/CompoundScope.js';
import { prefetchMedia, prefetchAround, probeUpscaled, loadUpscaled } from '../services/MediaPrefetch.js';

// Below this the two eye piece images are close enough to read as one circle.
const ALIGNED_THRESHOLD = 0.02;
// Blur at maximum, as a fraction of the circle diameter, so the softness looks
// the same whatever size the field is drawn at.
const MAX_BLUR_RATIO = 0.05;
const MAX_BLUR_PX = 24;
// How hard the slider blows the image out past the correct brightness.
const OVEREXPOSURE_GAIN = 3;
// Gap kept between the field and the edge of the view, in pixels.
const FIELD_PADDING = 8;
// How far the single-eye regions run past the overlap boundary, in pixels, so
// their anti-aliased edges meet rather than leave a gap.
const SEAM_BLEED = 0.75;

export default {
  name: 'MicroscopeZoom',
  mixins: [ImageMixin, NotificationMixin],
  data: function () {
    return {
      S3URL: Constants.S3_BUCKET_URL,
      JPG: Constants.IMG_EXT_JPG,
      THUMB: Constants.THUMBNAIL,
      F4V: Constants.IMG_EXT_F4V,
      MP4: Constants.IMG_EXT_MP4,
      PATH: Constants.MEDIA_CONTENT,
      scope,
      videoFrame: null,
      // What the student just clicked, shown straight away while its full-size
      // image is still downloading.
      pendingMedia: null,
      panLimit: { x: 0, y: 0 },
      panFrom: null,
      saveText: this.getLocaleData(BiologyLabEnum.MICROSCOPY, Constants.SAVE),
      compoundScopeText: this.getLocaleData(BiologyLabEnum.MICROSCOPY, Constants.COMPOUND_SCOPE),
    };
  },
  mounted() {
    if (this.currentMedia) {
      useImage(this.currentMedia.id);
      prefetchAround(this.images, this.images.indexOf(this.currentMedia));
      this.checkUpscaled(this.currentMedia);
    }
    this.$nextTick(() => {
      this.startSource();
    });
    window.addEventListener('resize', this.onResize);
  },
  unmounted() {
    window.removeEventListener('resize', this.onResize);
    this.stopVideoFrames();
  },
  methods: {
    onResize: function () {
      this.$nextTick(() => {
        this.render();
      });
    },
    /** Gets whatever the current media is ready to be drawn, then draws it. */
    startSource: function () {
      this.stopVideoFrames();
      if (this.isVideo && this.$refs.video) {
        const video = this.$refs.video;
        video.load();
        const play = video.play();
        if (play && play.catch) {
          play.catch(() => { });
        }
        this.drawVideoFrames();
        return;
      }
      this.render();
    },
    drawVideoFrames: function () {
      this.render();
      this.videoFrame = window.requestAnimationFrame(this.drawVideoFrames);
    },
    watchForFullImage: function (media) {
      if (media.type != 'static' || media.img) {
        return;
      }
      const poll = () => {
        if (this.pendingMedia !== media) {
          return;
        }
        if (media.img) {
          this.render();
          return;
        }
        window.setTimeout(poll, 120);
      };
      window.setTimeout(poll, 120);
    },
    stopVideoFrames: function () {
      if (this.videoFrame) {
        window.cancelAnimationFrame(this.videoFrame);
        this.videoFrame = null;
      }
    },
    prefetch: function (media) {
      prefetchMedia(media);
    },
    /**
     * Works out whether this slide has an upscaled copy, and fetches it if the
     * student is asking for one.
     */
    checkUpscaled: function (media) {
      if (!media) {
        return;
      }
      probeUpscaled(media).then((available) => {
        if (this.displayMedia !== media) {
          return;
        }
        if (available && scope.useUpscaled) {
          this.useUpscaledCopy(media);
        }
      });
    },
    useUpscaledCopy: function (media) {
      if (!media || !media.hasUpscaled) {
        return;
      }
      if (media.imgUpscaled) {
        this.render();
        return;
      }
      loadUpscaled(media);
      // It lands outside Vue's reactivity, so watch for it.
      const poll = () => {
        if (this.displayMedia !== media || !scope.useUpscaled) {
          return;
        }
        if (media.imgUpscaled) {
          this.render();
          return;
        }
        if (media.upscaledLoading) {
          window.setTimeout(poll, 150);
        }
      };
      window.setTimeout(poll, 150);
    },
    startPan: function (event) {
      if (!this.canPan) {
        return;
      }
      this.panFrom = { x: event.clientX, y: event.clientY, panX: scope.panX, panY: scope.panY };
      if (event.target.setPointerCapture) {
        event.target.setPointerCapture(event.pointerId);
      }
    },
    movePan: function (event) {
      if (!this.panFrom) {
        return;
      }
      // render() clamps these to whatever the current magnification allows.
      scope.panX = this.panFrom.panX + (event.clientX - this.panFrom.x);
      scope.panY = this.panFrom.panY + (event.clientY - this.panFrom.y);
      this.render();
    },
    endPan: function (event) {
      this.panFrom = null;
      if (event && event.target.releasePointerCapture && event.target.hasPointerCapture(event.pointerId)) {
        event.target.releasePointerCapture(event.pointerId);
      }
    },
    selectImage: function (media) {
      // Show the new slide immediately off its thumbnail. The store is not
      // updated until the full-size image has downloaded, so without this the
      // window sits on the previous slide for a second or more.
      this.pendingMedia = media;
      useImage(media.id);
      prefetchAround(this.images, this.images.indexOf(media));
      this.checkUpscaled(media);
      this.$nextTick(() => {
        this.startSource();
      });

      // These two do not depend on each other, so do not make the image wait on
      // the details call.
      MicroscopyMiddlewareServices.get_media_details(media).then(() => {
        entry(xapiEntryTypes.STATE, {
          extensions: {
            name: Constants.MICROSCOPE,
            state: media.title,
            inZoomPopup: true,
            scope: media.scope,
          },
        });
      });
      this.getMediaImageAndSetCurrentMedia(media).then(() => {
        if (this.pendingMedia === media) {
          this.pendingMedia = null;
        }
        this.$nextTick(() => {
          this.startSource();
        });
      });
    },
    /**
     * Draws the field as the student has it set up: the image scaled for the
     * objective in place, masked to a circle (two circles when the eye pieces
     * are out of alignment), blurred by how far focus is off and lit by the
     * brightness setting.
     */
    render: function () {
      const canvas = this.$refs.view;
      const wrapper = this.$refs.imageWrapper;
      if (!canvas || !wrapper) {
        return;
      }
      const side = Math.max(0, Math.min(wrapper.clientWidth, wrapper.clientHeight));
      const ratio = window.devicePixelRatio || 1;
      canvas.style.width = `${side}px`;
      canvas.style.height = `${side}px`;
      canvas.width = Math.round(side * ratio);
      canvas.height = Math.round(side * ratio);

      const ctx = canvas.getContext('2d');
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      ctx.clearRect(0, 0, side, side);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, side, side);

      const frame = this.sourceFrame();
      if (!frame || !side) {
        return;
      }

      // The field is always as large as the view allows; the specimen is what
      // grows as the objective changes.
      const diameter = side - FIELD_PADDING * 2;
      const radius = diameter / 2;
      if (radius <= 0) {
        return;
      }

      // At the lowest objective the specimen exactly covers the field, so its
      // shorter side matches the diameter and no empty space can show inside
      // the circle. Each step up draws it proportionally larger.
      const cover = diameter / Math.min(frame.width, frame.height);
      const drawScale = cover * magnificationZoom();
      const drawWidth = frame.width * drawScale;
      const drawHeight = frame.height * drawScale;

      // Dragging may not pull the specimen far enough to uncover the field.
      const panLimitX = Math.max(0, (drawWidth - diameter) / 2);
      const panLimitY = Math.max(0, (drawHeight - diameter) / 2);
      this.panLimit = { x: panLimitX, y: panLimitY };
      const panX = Math.max(-panLimitX, Math.min(panLimitX, scope.panX));
      const panY = Math.max(-panLimitY, Math.min(panLimitY, scope.panY));
      if (panX !== scope.panX) {
        scope.panX = panX;
      }
      if (panY !== scope.panY) {
        scope.panY = panY;
      }

      // Separation of 1 puts the edge of each circle on the centre of the other.
      const separated = separation();
      const gap = separated * radius;
      const centerX = side / 2;
      const centerY = side / 2;
      const centers = separated < ALIGNED_THRESHOLD
        ? [centerX]
        : [centerX - gap / 2, centerX + gap / 2];

      const blurPx = Math.min(MAX_BLUR_PX, blurAmount() * diameter * MAX_BLUR_RATIO);
      const filter = `blur(${blurPx.toFixed(2)}px) brightness(${this.brightnessLevel})`;

      const draw = (x, alpha) => {
        ctx.filter = filter;
        ctx.globalAlpha = alpha;
        ctx.drawImage(
          frame.element,
          x - drawWidth / 2 + panX,
          centerY - drawHeight / 2 + panY,
          drawWidth,
          drawHeight,
        );
      };
      const disc = (x) => {
        const path = new Path2D();
        path.arc(x, centerY, radius, 0, Math.PI * 2);
        return path;
      };
      // A rectangle with the disc punched out of it, so clipping to it with the
      // even-odd rule leaves everything outside that eye. Shrinking the hole
      // lets the caller bleed past the true edge.
      const outsideDisc = (x, shrink) => {
        const path = new Path2D();
        path.rect(0, 0, side, side);
        path.arc(x, centerY, Math.max(0, radius - (shrink || 0)), 0, Math.PI * 2);
        return path;
      };
      const field = () => {
        ctx.filter = 'none';
        ctx.globalAlpha = 1;
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, side, side);
      };

      if (centers.length === 1) {
        ctx.save();
        ctx.clip(disc(centers[0]));
        field();
        draw(centers[0], 1);
        ctx.restore();
        return;
      }

      const [near, far] = centers;

      // Lay the backing down over both discs in one pass, so the seam between
      // the regions below has nothing bright to show through.
      ctx.save();
      const both = disc(near);
      both.addPath(disc(far));
      ctx.clip(both);
      field();
      ctx.restore();

      // Paint three regions that do not overlap, each exactly once. Drawing one
      // eye on top of the other leaves its clip edge sitting over the other
      // image, which is what made one of them read as the nearer one.
      const region = (clips, paint) => {
        ctx.save();
        clips.forEach(([path, rule]) => ctx.clip(path, rule));
        paint();
        ctx.restore();
      };

      // The single-eye regions run a fraction past the boundary so their
      // anti-aliased edge cannot fall short of the overlap's and let the black
      // backing show through as a dark hairline. The overlap is painted last,
      // so that sliver ends up with the blended value either way.
      region([[disc(near)], [outsideDisc(far, SEAM_BLEED), 'evenodd']], () => draw(near, 1));
      region([[disc(far)], [outsideDisc(near, SEAM_BLEED), 'evenodd']], () => draw(far, 1));
      // Half of each, which comes to the same thing whichever is drawn first.
      region([[disc(near)], [disc(far)]], () => {
        draw(near, 1);
        draw(far, 0.5);
      });
    },
    /** The still or the current video frame, with its natural size. */
    sourceFrame: function () {
      const media = this.displayMedia;
      if (!media) {
        return null;
      }
      if (this.isVideo) {
        const video = this.$refs.video;
        if (!video || !video.videoWidth) {
          return this.thumbnailFrame(media);
        }
        return { element: video, width: video.videoWidth, height: video.videoHeight };
      }
      // Fall back through upscaled copy, full size, then the list thumbnail, so
      // there is always something to show while the better one downloads.
      const image = (scope.useUpscaled && media.imgUpscaled) || media.img;
      if (!image || !image.width) {
        return this.thumbnailFrame(media);
      }
      return { element: image, width: image.width, height: image.height };
    },
    /**
     * The already-loaded list thumbnail, used as a stand-in until the full-size
     * image arrives. It is served cross-origin like the full image, so drawing
     * it does not taint the canvas and Save keeps working.
     */
    thumbnailFrame: function (media) {
      const thumb = this.$el && this.$el.querySelector(`#imagesList img[data-media-id="${media.id}"]`);
      if (!thumb || !thumb.naturalWidth) {
        return null;
      }
      return { element: thumb, width: thumb.naturalWidth, height: thumb.naturalHeight };
    },
    saveToLabBook: function () {
      const save = (image) => {
        try {
          LabBookMiddleware.lab_book('entry', {
            entry: image,
            type: 3,
          });
          this.showNotification('Image saved.');
          entry(xapiEntryTypes.STATE, {
            extensions: {
              name: Constants.MICROSCOPE,
              state: Constants.SAVE,
            },
          });
        } catch (e) {
          console.error(e);
        }
      };
      try {
        save(this.$refs.view.toDataURL('image/png'));
      } catch (e) {
        // A tainted canvas cannot be read directly, so fall back to a screen grab.
        html2canvas(this.$refs.imageWrapper, {
          allowTaint: true,
          useCORS: true,
        }).then((generatedCanvas) => {
          save(generatedCanvas.toDataURL('image/png'));
        });
      }
    },
  },
  computed: {
    ...mapGetters({
      species: GET_CURRENT_SPECIES,
      currentMedia: GET_CURRENT_MEDIA,
      specialMedia: GET_SPECIAL_MEDIA_LIST,
    }),
    images: function () {
      if (this.specialMedia.length) {
        return this.specialMedia.filter((media) => media.scope == 'compound');
      }
      if (!this.species || !this.species.media) {
        return [];
      }
      return this.species.media.compound || [];
    },
    canPan: function () {
      return this.panLimit.x > 0.5 || this.panLimit.y > 0.5;
    },
    panCursor: function () {
      if (!this.canPan) {
        return '';
      }
      return this.panFrom ? 'isGrabbing' : 'canGrab';
    },
    /** What the window is showing: the pending click, else the stored media. */
    displayMedia: function () {
      return this.pendingMedia || this.currentMedia;
    },
    isVideo: function () {
      return !!this.displayMedia && this.displayMedia.type != 'static';
    },
    /**
     * Bright = Pos - Default. Below the default the field fades to black at 0;
     * above it the image burns out.
     */
    brightnessLevel: function () {
      const adjustment = brightnessAmount();
      if (adjustment <= 0) {
        const ideal = scope.brightnessIdeal || 1;
        return Math.max(0, 1 + adjustment / ideal);
      }
      return 1 + adjustment * OVEREXPOSURE_GAIN;
    },
    /** Everything the drawing depends on, so one watcher can cover the lot. */
    renderKey: function () {
      return [
        this.displayMedia && this.displayMedia.id,
        this.displayMedia && !!this.displayMedia.img,
        scope.useUpscaled,
        this.displayMedia && !!this.displayMedia.imgUpscaled,
        scope.panX,
        scope.panY,
        scope.eyePiecePos,
        scope.eyePieceIdeal,
        scope.magnificationIndex,
        scope.focusPos,
        scope.focusIdeal,
        scope.brightnessPos,
        scope.brightnessIdeal,
      ].join('|');
    },
  },
  watch: {
    renderKey: function () {
      this.$nextTick(() => {
        this.render();
      });
    },
    currentMedia: function (media) {
      if (media) {
        useImage(media.id);
      }
      this.$nextTick(() => {
        this.startSource();
      });
    },
    // The full-size image lands on the media object outside Vue's reactivity,
    // so poll briefly for the swap from the stand-in thumbnail.
    pendingMedia: function (media) {
      if (media) {
        this.watchForFullImage(media);
      }
    },
  },
};
</script>
<style scoped>
.row {
  display: flex;
}

.w-full {
  width: 100%;
}

.column {
  display: flex;
  flex-direction: column;
}

.align-center {
  align-items: center;
}

#zoomControls {
  margin-top: 12px;
  margin-bottom: 12px;
}

.zoomWrapper {
  width: 100%;
  height: 100%;
  display: flex;
}

.leftColumn {
  flex-basis: auto;
  max-width: 80%;
}

.mediaName {
  margin-bottom: 12px;
}

#imageWrapper {
  width: 100%;
  height: 100%;
  overflow: hidden;
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

#scopeView {
  display: block;
  /* Let a touch drag pan the specimen rather than scroll the page. */
  touch-action: none;
}

#scopeView.canGrab {
  cursor: grab;
}

#scopeView.isGrabbing {
  cursor: grabbing;
}

.toggle {
  display: inline-flex;
  align-items: center;
  margin-left: 12px;
  font-size: 12px;
  color: #17469e;
  cursor: pointer;
  user-select: none;
}

.toggleInput {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.toggleTrack {
  width: 30px;
  height: 16px;
  border-radius: 8px;
  background: #a8b8d6;
  transition: background 0.15s ease;
  display: inline-flex;
  align-items: center;
  padding: 2px;
  box-sizing: border-box;
}

.toggleKnob {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.15s ease;
}

.toggleInput:checked + .toggleTrack {
  background: #17469e;
}

.toggleInput:checked + .toggleTrack .toggleKnob {
  transform: translateX(14px);
}

.toggleInput:focus-visible + .toggleTrack {
  outline: 2px solid #17469e;
  outline-offset: 2px;
}

.toggleLabel {
  margin-left: 6px;
}

/* Kept in the layout so the browser keeps decoding frames, but out of sight. */
#scopeSource {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

#imagesList {
  margin-left: 24px;
  overflow: auto;
  margin-top: 24px;
  min-width: 77px;
  margin-right: 42px;
  height: calc(100% - 50px);
}

.imageThumbnail {
  width: 50px;
  border: 1px solid;
  margin: 2px 0;
  cursor: pointer;
}

.selectedThumbnail {
  border: 2px solid yellow;
}

.btn {
  border: 1px solid #17469e;
  border-radius: 4px;
  font-size: 12px;
  background: #fff;
  color: #17469e;
  padding: 0 8px;
  margin: 6px;
  height: 25px;
  display: flex;
  align-items: center;
  cursor: pointer;
  width: fit-content;
}

.ml-0 {
  margin-left: 0px;
}


.btn-icon {
  height: 18px;
  color: #17469e;
  display: table-cell;
  vertical-align: middle;
}

.btn-icon-text {
  padding-right: 6px;
}

.ripple {
  background-position: center;
  transition: background 0.8s;
}

.ripple:hover {
  background: #fff radial-gradient(circle, transparent 1%, #fff 1%) center/15000%;
}

.ripple:active {
  background-color: #17469e7c;
  background-size: 100%;
  transition: background 0s;
}
</style>
